import * as React from 'react'

import { Slider as SliderPrimitive } from '@base-ui/react/slider'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

// The halo is the product focus ring: the focused element is the thumb's nested input, hence
// has-focus-visible rather than focus-visible on the thumb itself.
const sliderThumbVariants = cva(
  'tw:relative tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-ring tw:outline-none tw:select-none tw:ring-ring/50 tw:transition-[color,box-shadow] tw:after:absolute tw:after:-inset-2 tw:has-focus-visible:ring-3 tw:data-dragging:ring-3',
  {
    variants: {
      size: {
        default: 'tw:size-4 tw:bg-background tw:hover:not-data-disabled:ring-3',
        lg: 'tw:size-6 tw:bg-primary tw:[&_svg]:size-4',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
)

function Slider({
  className,
  children,
  size,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  getAriaValueText,
  ...props
}: SliderPrimitive.Root.Props<number> &
  Pick<SliderPrimitive.Thumb.Props, 'getAriaValueText'> &
  VariantProps<typeof sliderThumbVariants>) {
  return (
    <SliderPrimitive.Root
      data-slot="slider"
      className={cn(
        'tw:relative tw:flex tw:touch-none tw:items-center tw:select-none tw:data-disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Control
        data-slot="slider-control"
        className="tw:relative tw:flex tw:h-full tw:w-full tw:items-center"
      >
        <SliderPrimitive.Track
          data-slot="slider-track"
          className="tw:relative tw:h-1 tw:w-full tw:overflow-hidden tw:rounded-full tw:bg-muted"
        >
          <SliderPrimitive.Indicator
            data-slot="slider-range"
            className="tw:h-full tw:rounded-full tw:bg-primary tw:select-none"
          />
        </SliderPrimitive.Track>
        {/* children render inside the thumb so consumers can drop in a glyph */}
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          getAriaValueText={getAriaValueText}
          className={sliderThumbVariants({ size })}
        >
          {children}
        </SliderPrimitive.Thumb>
      </SliderPrimitive.Control>
    </SliderPrimitive.Root>
  )
}

export { Slider }
